import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "Why don\u2019t you show fixed prices?",
    answer:
      "Because StackMap solutions are modular and pricing depends on your selected products, modules, business size, and implementation needs. Every organisation has a unique combination of requirements, and we believe in giving you a plan that fits rather than forcing you into a rigid box. Reach out and we will put together a tailored quote for your specific situation.",
  },
  {
    question: "Can I subscribe to only one module?",
    answer:
      "Yes. Businesses can start with selected modules and expand as their needs grow. You are never locked into buying more than you need. Many of our customers begin with one or two core apps and add more over time as they see the operational improvements firsthand.",
  },
  {
    question: "Can I upgrade my plan over time?",
    answer:
      "Absolutely. You can upgrade from Start to Grow, Grow to Transform, or Transform to Enterprise at any point. Upgrades are seamless — your data, configurations, and integrations carry forward without disruption. We also offer prorated billing so you only pay the difference for the remainder of your billing cycle.",
  },
  {
    question: "Do you offer implementation support?",
    answer:
      "Yes. Implementation, onboarding, training, and support can be included based on the selected plan. Our team works with you to ensure a smooth rollout, from initial configuration and data migration to team training and go-live support. Enterprise plans include a dedicated implementation manager.",
  },
  {
    question: "How long does implementation typically take?",
    answer:
      "Implementation timelines vary based on scope, but most organisations are operational within 2 to 6 weeks. Start plans with standard configurations can go live in as little as one week. Enterprise deployments with custom integrations and multi-branch rollouts typically take 4 to 8 weeks with phased go-live milestones.",
  },
  {
    question: "Can StackMap integrate with our existing systems?",
    answer:
      "Yes. StackMap supports native integrations with common business tools and platforms. For proprietary or legacy systems, our Grow plan and above include API access for custom integrations. Enterprise plans include dedicated integration engineering support to connect StackMap with your full technology stack.",
  },
  {
    question: "Is user training provided?",
    answer:
      "Yes. Every plan includes access to our self-service knowledge base and video library. Grow plans add guided onboarding sessions. Transform and Enterprise plans include comprehensive role-based training for your entire team, train-the-trainer programs, and ongoing refresher sessions as new features are released.",
  },
  {
    question: "Is pricing monthly or annual?",
    answer:
      "Pricing can be structured monthly, annually, or as a project-based engagement depending on the product and scope. Annual plans typically come with cost advantages, while monthly plans offer flexibility. For bespoke or large-scale implementations, we also offer milestone-based project pricing.",
  },
  {
    question: "Can StackMap support multi-branch businesses?",
    answer:
      "Yes. StackMap supports multi-location and multi-branch businesses depending on the selected package. Our Transform plan includes full multi-branch administration, consolidated reporting across locations, and role-based access that lets each branch operate independently while headquarters maintains complete visibility.",
  },
];

export default function PricingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="relative py-16 md:py-24 bg-graphite-900">
      <div className="section-padding">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-10 md:mb-14">
            Frequently Asked{" "}
            <span className="text-emerald-accent">Questions</span>
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={faq.question}
                  className="glass-card rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-white pr-4">
                      {faq.question}
                    </span>
                    <span className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i
                        className={`ri-add-line text-emerald-accent text-base transition-transform duration-300 ${
                          isOpen ? "rotate-45" : ""
                        }`}
                      />
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="px-5 pb-4 text-sm text-silver-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}